// White card design for the dashboard quick stats grid.
// Thin border, soft shadow, big number, soft-bg colored icon.

// Color palette per stat "color" key — used for icon tint, soft icon bg
// and optional badge tint.
export const STAT_PALETTE = {
  primary: { fg: '#3a5af0', soft: 'rgba(58,90,240,0.12)' },
  success: { fg: '#18a558', soft: 'rgba(24,165,88,0.12)' },
  warning: { fg: '#e0a106', soft: 'rgba(224,161,6,0.14)' },
  error:   { fg: '#d64545', soft: 'rgba(214,69,69,0.12)' },
  info:    { fg: '#0ea5e9', soft: 'rgba(14,165,233,0.12)' },
  purple:  { fg: '#7c5cf0', soft: 'rgba(124,92,240,0.12)' },
};

// Badge tint per "badgeTone" — independent from the icon color so cards
// can show e.g. a green "Active" badge on a blue-tinted card.
export const BADGE_TONES = {
  success: { fg: '#18a558', bg: 'rgba(24,165,88,0.12)' },
  warning: { fg: '#e0a106', bg: 'rgba(224,161,6,0.14)' },
  error:   { fg: '#d64545', bg: 'rgba(214,69,69,0.12)' },
  neutral: { fg: '#626b78', bg: '#f4f5f7' },
};

const palette = (color) => STAT_PALETTE[color] || STAT_PALETTE.primary;
const tone = (t) => BADGE_TONES[t] || BADGE_TONES.neutral;

export const statsCardStyles = {
  card: {
    height: '100%',
    borderRadius: '16px',
    background: '#ffffff',
    border: '1px solid #e8eaee',
    boxShadow: '0 1px 2px rgba(20,30,55,.04)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: '#d8dce4',
      boxShadow: '0 10px 24px rgba(20,30,55,.08)',
    },
  },

  content: {
    p: '20px !important',
    '&:last-child': { pb: '20px !important' },
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
  },

  topRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1,
  },

  iconBox: (color) => {
    const p = palette(color);
    return {
      width: 42,
      height: 42,
      borderRadius: '12px',
      background: `linear-gradient(135deg, ${p.soft}, ${p.soft.replace(/0\.\d+\)$/, '0.04)')})`,
      border: `1px solid ${p.soft}`,
      color: p.fg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    };
  },

  badge: (toneKey) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1,
    py: 0.4,
    borderRadius: '999px',
    background: tone(toneKey).bg,
    color: tone(toneKey).fg,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  }),

  badgeIcon: {
    fontSize: '0.85rem',
  },

  value: {
    fontSize: '34px',
    fontWeight: 800,
    color: '#0f1424',
    lineHeight: 1.05,
    letterSpacing: '-1px',
    fontVariantNumeric: 'tabular-nums',
    mt: 0.5,
  },

  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#626b78',
    letterSpacing: '-0.1px',
  },
};
